import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';

ChartJS.register(...registerables);

const ReportedIssuesByCategoryChart = ({ data = [] }) => {
    const reportedIssuesChartRef = useRef(null);
    const reportedIssuesChartInstance = useRef(null);

    // Default data if none provided
    const defaultData = [
        { category: 'Jan', count: 48 },
        { category: 'Feb', count: 38 },
        { category: 'Mar', count: 58 },
        { category: 'Apr', count: 48 },
        { category: 'May', count: 68 },
        { category: 'Jun', count: 68 },
    ];

    const chartData = data.length > 0 ? data : defaultData;
    const labels = chartData.map(item => item.category || item.label || item.month);
    const values = chartData.map(item => item.count || item.issues || item.value || 0);
    const maxValue = Math.max(...values, 10);

    useEffect(() => {
        if (reportedIssuesChartRef.current) {
            if (reportedIssuesChartInstance.current) {
                reportedIssuesChartInstance.current.destroy();
            }

            // Calculate appropriate y-axis max
            const stepSize = Math.ceil(maxValue / 4 / 10) * 10 || 10;
            const yMax = Math.ceil(maxValue * 1.2 / stepSize) * stepSize;

            const ctx = reportedIssuesChartRef.current.getContext('2d');
            reportedIssuesChartInstance.current = new ChartJS(ctx, {
                type: 'bar',
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: 'Issues',
                            data: values,
                            backgroundColor: '#e1dcf9', // Soft lavender
                            borderColor: '#a396e0',     // Violet border
                            borderWidth: 1,
                            borderRadius: 0,           // Square corners
                            barThickness: 32,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            enabled: true,
                            backgroundColor: '#333',
                            titleFont: { family: 'Inter, sans-serif' },
                            bodyFont: { family: 'Inter, sans-serif' },
                            callbacks: {
                                label: (context) => `Issues: ${context.raw}`
                            }
                        },
                        datalabels: { display: false },
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            border: {
                                display: true,
                                color: '#333333',
                            },
                            ticks: {
                                color: '#333333',
                                font: { size: 12, family: 'Inter, sans-serif' },
                                padding: 8,
                            },
                        },
                        y: {
                            beginAtZero: true,
                            max: yMax,
                            ticks: {
                                stepSize: stepSize,
                                color: '#666666',
                                font: { size: 12, family: 'Inter, sans-serif' },
                                padding: 8,
                            },
                            grid: {
                                color: '#e0e0e0',
                                drawOnChartArea: true,
                                drawTicks: true
                            },
                            border: {
                                display: true,
                                color: '#333333',
                            },
                        },
                    },
                },
            });
        }
        return () => {
            if (reportedIssuesChartInstance.current) {
                reportedIssuesChartInstance.current.destroy();
            }
        };
    }, [data, labels, values, maxValue]);

    const hasData = chartData.some(item => (item.count || item.issues || item.value || 0) > 0);

    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                bgcolor: '#ffffff',
                border: '1px solid #e6e6e6',
                borderRadius: '20px',
                padding: '20px',
                gap: '10px'
            }}
        >
            {/* Vertical Label */}
            <Typography
                sx={{
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                    color: '#333333',
                    fontSize: '14px',
                    fontWeight: 500,
                    fontFamily: 'Inter, sans-serif',
                    whiteSpace: 'nowrap'
                }}
            >
                No. of Issues
            </Typography>
            <Box sx={{ flex: 1, height: '100%', position: 'relative' }}>
                {!hasData && data.length === 0 ? (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%'
                    }}>
                        <Typography sx={{ color: '#999', fontSize: '14px' }}>
                            No reported issues data available
                        </Typography>
                    </Box>
                ) : (
                    <canvas ref={reportedIssuesChartRef} style={{ width: '100%', height: '100%' }} />
                )}
            </Box>
        </Box>
    );
};

export default ReportedIssuesByCategoryChart;
